import React, { FC } from 'react';
import ActorTypeSelector from './ActorTypeSelector';
import { useTranslation } from 'react-i18next';
import { ActorType } from '../../enums/actorType';
import { NaturalPersonLayout } from './NaturalPersonLayout';
import { LegalEntityLayout } from './LegalEntityLayout';
import { ActorLayoutVariant } from '../../enums/actorLayoutVariant';
import { BaseLayoutProps, LegalEntityOptions } from 'types/form/baseLayoutProps';
import { NsKey } from 'enums/NsKey';
import { LegalEntityContactLayout } from './LegalEntityContactLayout';

interface ActorDynamicLayoutProps {
  layoutVariant: ActorLayoutVariant;
  actorType: ActorType;
  handleActorTypeChange: (type: ActorType) => void;
  isEdit?: boolean;
  /** i18n keys overriding the variant title, when the same layout serves another role (e.g. MRI representative). */
  titleKeys?: { create: string; edit: string };
  /** Opt-in blocks of the legal entity layout. */
  legalEntityOptions?: LegalEntityOptions;
}

const mapFormLayout: Partial<Record<ActorType, FC<BaseLayoutProps>>> = {
  [ActorType.Legal]: LegalEntityLayout,
  [ActorType.Natural]: NaturalPersonLayout,
  [ActorType.NaturalWithDocuments]: NaturalPersonLayout
};

const mapTitle: Partial<Record<ActorLayoutVariant, string>> = {
  [ActorLayoutVariant.DefaultActor]: 'ACTOR_FORM.LABELS.CREATE_ACTOR_TITLE',
  [ActorLayoutVariant.NaturalPerson]: '',
  [ActorLayoutVariant.InsuredActor]: 'ACTOR_FORM.LABELS.CREATE_INSURED_ACTOR_TITLE',
  [ActorLayoutVariant.UnemploymentActor]:
    'ACTOR_FORM.LABELS.CREATE_INSURED_ACTOR_TITLE',
  [ActorLayoutVariant.ActorWithDefaultDocumentsActor]:
    'ACTOR_FORM.LABELS.CREATE_INSURED_ACTOR_TITLE',
  [ActorLayoutVariant.UpiActor]: 'ACTOR_FORM.LABELS.CREATE_INSURED_ACTOR_TITLE',
  [ActorLayoutVariant.LegalProtectionActor]: 'ACTOR_FORM.LABELS.CREATE_INSURED_ACTOR_TITLE',
  [ActorLayoutVariant.LegalEntityContact]:
    'ACTOR_FORM.LABELS.CREATE_INSURED_ACTOR_TITLE'
};

const editMapTitle: Partial<Record<ActorLayoutVariant, string>> = {
  [ActorLayoutVariant.DefaultActor]: 'ACTOR_FORM.LABELS.CREATE_ACTOR_TITLE',
  [ActorLayoutVariant.NaturalPerson]: '',
  [ActorLayoutVariant.InsuredActor]: 'ACTOR_FORM.LABELS.EDIT_INSURED_ACTOR_TITLE',
  [ActorLayoutVariant.UnemploymentActor]:
    'ACTOR_FORM.LABELS.EDIT_INSURED_ACTOR_TITLE',
  [ActorLayoutVariant.ActorWithDefaultDocumentsActor]:
    'ACTOR_FORM.LABELS.EDIT_INSURED_ACTOR_TITLE',
  [ActorLayoutVariant.UpiActor]: 'ACTOR_FORM.LABELS.EDIT_INSURED_ACTOR_TITLE',
  [ActorLayoutVariant.LegalProtectionActor]: 'ACTOR_FORM.LABELS.EDIT_INSURED_ACTOR_TITLE',
  [ActorLayoutVariant.LegalEntityContact]:
    'ACTOR_FORM.LABELS.CREATE_INSURED_ACTOR_TITLE'
};

export const ActorDynamicLayout: React.FC<ActorDynamicLayoutProps> = ({
  layoutVariant,
  handleActorTypeChange,
  actorType,
  isEdit = false,
  titleKeys,
  legalEntityOptions
}) => {
  const { t } = useTranslation(NsKey.ActorForm);

  const SelectedLayout = layoutVariant === ActorLayoutVariant.LegalEntityContact
    ? LegalEntityContactLayout
    : mapFormLayout[actorType];

  const renderSelector = () => {
    if (layoutVariant === ActorLayoutVariant.UnemploymentActor) return;

    if (layoutVariant === ActorLayoutVariant.LegalProtectionActor) return;

    if (layoutVariant === ActorLayoutVariant.ActorWithDefaultDocumentsActor)
      return;

    if (layoutVariant === ActorLayoutVariant.UpiActor && isEdit) return;

    return (
      <ActorTypeSelector value={actorType} onChange={handleActorTypeChange} />
    );
  };

  const renderHeader = () => {
    if (
      layoutVariant === ActorLayoutVariant.NaturalPerson ||
      layoutVariant === ActorLayoutVariant.ActorWithDefaultDocumentsActor ||
      layoutVariant === ActorLayoutVariant.LegalEntityContact ||
      // Rendered in a side panel that already carries the title, with no actor type to pick.
      layoutVariant === ActorLayoutVariant.LegalProtectionActor
    ) {
      return <></>;
    }

    const titleMap = isEdit ? editMapTitle : mapTitle;
    const titleKey = titleKeys ? (isEdit ? titleKeys.edit : titleKeys.create) : titleMap[layoutVariant];
    return (
      <div className="flex items-center mb-4">
        <p className="text-gray-700 flex-grow text-3xl leading-9 font-bold">
          {t(titleKey || '')}
        </p>
        {renderSelector()}
      </div>
    );
  };

  return (
    <div>
      {renderHeader()}
      {SelectedLayout && (
        <SelectedLayout
          key={actorType}
          isEdit={isEdit}
          layoutVariant={layoutVariant}
          legalEntityOptions={legalEntityOptions}
        />
      )}
    </div>
  );
};
